import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import logoFace from '../../assets/logo-face.png';

// MembershipCelebration — the mark on /membership/success.
//
// ── WHAT IT REPLACED, AND WHY ───────────────────────────────────────────────
//
// A <CensorBar mode="LABEL" value="MEMBER" />: a black box with a word in it,
// identical to the one on the CANCEL screen two routes away. The single moment
// a member has just paid for something looked exactly like the moment they
// decided not to.
//
// The replacement is a seal, not a badge. An aurum hairline ring DRAWS ITSELF
// clockwise around the crest, and gold leaf falls through it once. Same ring
// vocabulary as ScanRevealModal's dial and LogoSpinner's arc, so the surface
// reads as this system rather than a stock success page.
//
//   ╭────────╮   ring: strokeDashoffset → 0 over RING_MS, clockwise
//  │    ♞    │   crest: scales in under it
//   ╰────────╯   leaf: LEAF_COUNT slivers, fan out, drift down, fade
//
// ── NOT CONFETTI ────────────────────────────────────────────────────────────
//
// No package. framer-motion is already a dependency and this is ~20 animated
// spans; canvas-confetti would be a new dep for a one-shot on one screen, and
// the primary-colour party shapes it ships would be the first thing on any
// surface here that is not Obsidian, Bone or Aurum.
//
// Everything is ONE SHOT. Nothing loops: a page that keeps twinkling behind a
// receipt stops reading as celebration and starts reading as an unfinished
// load.

const AURUM = '#C9A45C';
const AURUM_PALE = '#EDD3A2';

const RING_MS = 0.7;
const LEAF_COUNT = 20;
const SIZE = 132;
const STROKE = 1.5;
const R = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * R;

// Deterministic, so the leaf pattern is the same every render and in every
// snapshot. Math.random() here would make the component untestable for nothing
// — the fan only has to look scattered, not be scattered.
function leaf(i) {
  const spread = (i / LEAF_COUNT) * Math.PI * 2;
  // Two co-prime multipliers keep distance and size from marching in step with
  // the angle, which is what makes a ring of evenly-spaced dots read as random.
  // Wide enough that the fan clears the crest rather than settling on top of
  // it — at the tighter radius this started on, the leaf read as a burst behind
  // the face instead of gold falling past a seal.
  const distance = 128 + ((i * 37) % 112);
  const drift = 74 + ((i * 53) % 104);
  return {
    x: Math.cos(spread) * distance,
    y: Math.sin(spread) * distance * 0.55 + drift,
    rotate: ((i * 71) % 360) - 180,
    width: 2 + ((i * 13) % 4),
    height: 9 + ((i * 29) % 12),
    delay: 0.12 + (i % 7) * 0.045,
    duration: 1.5 + ((i * 17) % 90) / 100,
    // Alternating tones so the fall has depth rather than reading as one flat
    // colour. Both are on the Aurum allow-list.
    //
    // Literals, not var(--color-aurum): these are set through React's style
    // object, and a var() in a shorthand is dropped outright by jsdom — the
    // leaf would render correctly in a browser and be colourless in every test.
    // LogoSpinner and ScanRevealModal hardcode the same hex for the same
    // reason. If the token moves, DESIGN.md §2 is the place these follow.
    color: i % 3 === 0 ? AURUM_PALE : AURUM,
  };
}

const LEAVES = Array.from({ length: LEAF_COUNT }, (_, i) => leaf(i));

export default function MembershipCelebration() {
  // Under prefers-reduced-motion the ring is rendered COMPLETE and the leaf is
  // not rendered at all. The seal is the information; the drawing of it is the
  // decoration, and only the decoration is negotiable.
  const reduce = useReducedMotion();

  return (
    <div
      className="relative flex items-center justify-center"
      style={{ width: SIZE, height: SIZE }}
      data-testid="membership-celebration"
    >
      {/* Leaf sits under the seal so a sliver never crosses the crest's face. */}
      {!reduce && (
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          {LEAVES.map((l, i) => (
            <motion.span
              key={i}
              data-testid="celebration-leaf"
              className="absolute left-1/2 top-1/2 block rounded-[1px]"
              style={{ width: l.width, height: l.height, background: l.color }}
              initial={{ x: 0, y: 0, opacity: 0, rotate: 0, scale: 0.4 }}
              animate={{ x: l.x, y: l.y, opacity: [0, 1, 1, 0], rotate: l.rotate, scale: 1 }}
              transition={{
                duration: l.duration,
                delay: l.delay,
                ease: 'easeOut',
                // Hold full opacity through most of the fall, then fade — a
                // linear fade from the first frame reads as a fizzle.
                opacity: { times: [0, 0.12, 0.62, 1], duration: l.duration, delay: l.delay },
              }}
            />
          ))}
        </div>
      )}

      <svg
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        fill="none"
        aria-hidden="true"
        // -90° so the stroke starts at 12 o'clock and travels clockwise.
        className="absolute inset-0 -rotate-90"
      >
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={R}
          stroke="rgba(255,255,255,0.08)"
          strokeWidth={STROKE}
        />
        <motion.circle
          data-testid="celebration-ring"
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={R}
          stroke={AURUM}
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          initial={{ strokeDashoffset: reduce ? 0 : CIRCUMFERENCE }}
          animate={{ strokeDashoffset: 0 }}
          transition={{ duration: reduce ? 0 : RING_MS, ease: 'easeInOut' }}
          style={{ filter: 'drop-shadow(0 0 8px rgba(201,164,92,0.45))' }}
        />
      </svg>

      {/* mix-blend-screen drops the crest's black plate onto the page ground,
          the same treatment HeaderAccount and LogoSpinner use for this asset. */}
      <motion.img
        src={logoFace}
        alt=""
        aria-hidden="true"
        className="relative object-contain mix-blend-screen"
        style={{ width: SIZE * 0.52, height: SIZE * 0.52 }}
        initial={{ opacity: reduce ? 1 : 0, scale: reduce ? 1 : 0.86 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: reduce ? 0 : 0.55, delay: reduce ? 0 : 0.18, ease: 'easeOut' }}
      />
    </div>
  );
}
